/**
 * Setup 2FA Modal
 *
 * Modal para configurar autenticación de dos factores
 * Muestra código QR, secreto para entrada manual, y códigos de respaldo
 */

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Shield,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  Download,
  QrCode,
  Key,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { useSetup2FA, useVerify2FA } from '@/application/auth/useTwoFactor'
import { toast } from '@/lib/toast'

// =============================================================================
// Schema
// =============================================================================

const verifySchema = z.object({
  code: z.string().min(6, 'El código debe tener 6 dígitos').max(10),
})

type VerifyForm = z.infer<typeof verifySchema>

// =============================================================================
// Types
// =============================================================================

interface Setup2FAModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

type Step = 'loading' | 'scan' | 'verify' | 'backup' | 'complete'

// =============================================================================
// Component
// =============================================================================

export function Setup2FAModal({ open, onOpenChange, onSuccess }: Setup2FAModalProps) {
  const [step, setStep] = useState<Step>('loading')
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)
  const [copiedBackup, setCopiedBackup] = useState(false)

  const setupMutation = useSetup2FA()
  const verifyMutation = useVerify2FA()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VerifyForm>({
    resolver: zodResolver(verifySchema),
  })

  // Iniciar setup cuando se abre el modal
  const handleOpen = async () => {
    setStep('loading')
    setError(null)
    reset()

    try {
      const result = await setupMutation.mutateAsync()
      setQrCode(result.qrCode)
      setSecret(result.secret)
      setBackupCodes(result.backupCodes)
      setStep('scan')
    } catch (err: any) {
      // Si es error de autenticación, mostrar mensaje claro
      if (err.response?.status === 401) {
        setError('Tu sesión ha expirado. Por favor, cierra este diálogo y vuelve a iniciar sesión.')
      } else {
        const message = err.response?.data?.message || err.message || 'Error iniciando configuración'
        setError(message)
      }
      setStep('scan') // Mostrar error en el paso scan
    }
  }

  // Llamar handleOpen cuando el modal se abre
  useEffect(() => {
    if (open) {
      handleOpen()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Verificar código
  const onVerify = async (data: VerifyForm) => {
    setError(null)

    try {
      const result = await verifyMutation.mutateAsync(data.code)
      if (result.verified) {
        setStep('backup')
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Código inválido'
      setError(message)
    }
  }

  // Copiar secreto
  const copySecret = async () => {
    await navigator.clipboard.writeText(secret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 2000)
  }

  // Copiar códigos de respaldo
  const copyBackupCodes = async () => {
    const text = backupCodes.join('\n')
    await navigator.clipboard.writeText(text)
    setCopiedBackup(true)
    setTimeout(() => setCopiedBackup(false), 2000)
  }

  // Descargar códigos de respaldo
  const downloadBackupCodes = () => {
    const text = `JH Lumina - Códigos de Respaldo 2FA\n${'='.repeat(40)}\n\n${backupCodes.join('\n')}\n\n⚠️ Guarde estos códigos en un lugar seguro.\nCada código solo puede usarse una vez.`
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'jh-lumina-backup-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Completar
  const handleComplete = () => {
    setStep('complete')
    toast.success('2FA activado correctamente')
    onSuccess?.()
    setTimeout(() => {
      onOpenChange(false)
      // Reset state para próxima vez
      setStep('loading')
      setQrCode('')
      setSecret('')
      setBackupCodes([])
    }, 2000)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[var(--primary)]" />
            Configurar Autenticación de Dos Factores
          </DialogTitle>
          <DialogDescription>
            Agrega una capa extra de seguridad a tu cuenta
          </DialogDescription>
        </DialogHeader>

        {/* Loading */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mb-4" />
            <p className="text-[var(--foreground-muted)]">Generando configuración...</p>
          </div>
        )}

        {/* Step 1: Scan QR */}
        {step === 'scan' && (
          <div className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)]">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="text-center">
              <p className="text-sm text-[var(--foreground-muted)] mb-4">
                Escanea el código QR con tu aplicación de autenticación
                (Google Authenticator, Authy, etc.)
              </p>

              {qrCode && (
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-lg">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>
              )}

              <div className="text-sm text-[var(--foreground-muted)]">
                <p className="mb-2">¿No puedes escanear? Ingresa este código manualmente:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="px-3 py-2 bg-[var(--background-subtle)] rounded font-mono text-sm">
                    {secret}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={copySecret}
                    className="shrink-0"
                  >
                    {copiedSecret ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={() => setStep('verify')}>
                Continuar
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 'verify' && (
          <form onSubmit={handleSubmit(onVerify)} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)]">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="text-center">
              <QrCode className="h-12 w-12 text-[var(--primary)] mx-auto mb-4" />
              <p className="text-sm text-[var(--foreground-muted)] mb-4">
                Ingresa el código de 6 dígitos de tu aplicación de autenticación
                para verificar la configuración.
              </p>
            </div>

            <Input
              label="Código de verificación"
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
              error={errors.code?.message}
              {...register('code')}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('scan')}>
                Volver
              </Button>
              <Button type="submit" disabled={verifyMutation.isPending}>
                {verifyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Verificando...
                  </>
                ) : (
                  'Verificar'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Step 3: Backup Codes */}
        {step === 'backup' && (
          <div className="space-y-6">
            <div className="text-center">
              <Key className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Códigos de Respaldo</h3>
              <p className="text-sm text-[var(--foreground-muted)]">
                Guarda estos códigos en un lugar seguro. Los necesitarás si pierdes
                acceso a tu aplicación de autenticación.
              </p>
            </div>

            <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code
                    key={index}
                    className="px-3 py-2 bg-[var(--background)] rounded text-center font-mono text-sm"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyBackupCodes}
              >
                {copiedBackup ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadBackupCodes}
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
            </div>

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm">
              <strong>Importante:</strong> Cada código solo puede usarse una vez.
              No podrás ver estos códigos de nuevo.
            </div>

            <DialogFooter>
              <Button onClick={handleComplete} className="w-full">
                He guardado mis códigos
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Complete */}
        {step === 'complete' && (
          <div className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
            <h3 className="font-semibold text-lg mb-2">¡2FA Activado!</h3>
            <p className="text-sm text-[var(--foreground-muted)] text-center">
              Tu cuenta ahora está protegida con autenticación de dos factores.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
